import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { http, formatError } from "@/lib/api";
import { getAttribution, trackLead } from "@/lib/tracking";

const BASE_OPTIONS = ["Google Ads", "Facebook & Instagram Ads", "SEO", "Google Business Profile", "Website Design", "Not Sure Yet"];
const inputCls = "w-full border-0 border-b-2 border-violet-200 bg-transparent px-0 py-3 text-xl font-semibold text-[#13052A] placeholder:font-medium placeholder:text-slate-400 transition-colors focus:border-[#7C3AED] focus:outline-none focus:ring-0 sm:text-2xl";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEPS = [
  { key: "name", q: "What's your name?", placeholder: "Your name", auto: "name" },
  { key: "website", q: "What's your business website?", placeholder: "www.yourbusiness.co.uk", auto: "url", hint: "No website yet? Just type \u201cnone yet\u201d." },
  { key: "services", q: "What would you like help with?", hint: "Choose as many as you like." },
  { key: "challenge", q: "What's your biggest marketing challenge?", optional: true, placeholder: "e.g. we get clicks but not enough enquiries" },
  { key: "email", q: "What's your email address?", placeholder: "you@yourbusiness.co.uk", auto: "email" },
];

function stepError(key, f, services) {
  if (key === "name" && f.name.trim().length < 2) return "Please tell me your name.";
  if (key === "website" && f.website.trim().length < 3) return "Please add your website, or type \u201cnone yet\u201d.";
  if (key === "services" && !services.length) return "Please choose at least one option.";
  if (key === "email" && !EMAIL_RE.test(f.email.trim())) return "Please enter a valid email address.";
  if (key === "email" && !f.consent) return "Please confirm you're happy for me to use these details to reply.";
  return "";
}

const Progress = ({ step }) => (
  <div className="flex items-center gap-3" aria-label={`Step ${step + 1} of ${STEPS.length}`}>
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-violet-100">
      <motion.div className="h-full rounded-full bg-[#7C3AED]" animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.4 }} />
    </div>
    <span className="text-xs font-bold text-[#581C87]" data-testid="form-progress">{step + 1} / {STEPS.length}</span>
  </div>
);

const Chips = ({ options, services, toggle }) => (
  <div className="mt-6 grid gap-2 sm:grid-cols-2" data-testid="form-services" role="group" aria-label="Services">
    {options.map((s) => {
      const on = services.includes(s);
      return (
        <button type="button" key={s} onClick={() => toggle(s)} aria-pressed={on} data-testid={`form-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={`flex min-h-[48px] items-center justify-between gap-2 rounded-2xl border-2 px-4 py-3 text-left text-[15px] font-semibold transition-colors ${on ? "border-[#7C3AED] bg-[#F5F0FF] text-[#2E1065]" : "border-violet-100 bg-white text-[#2E1065] hover:border-violet-300"}`}>
          {s}
          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-violet-200"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
        </button>
      );
    })}
  </div>
);

const Success = ({ name }) => (
  <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} data-testid="enquiry-success" className="rounded-[28px] bg-white p-8 text-center sm:p-12">
    <CheckCircle2 className="mx-auto h-14 w-14 text-[#7C3AED]" />
    <h3 className="mt-6 text-2xl font-extrabold text-[#13052A] sm:text-3xl">Thank you, {name.split(" ")[0]}!</h3>
    <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-slate-600">
      Your request has come straight to me. I'll take a personal look and get back to you, usually within one to two working days.
    </p>
    <p className="serif-accent mt-4 text-xl text-[#581C87]">Let's grow together.</p>
  </motion.div>
);

export const StepForm = ({ defaultServices = [], formType = "audit", submitLabel = "Request My Free Audit" }) => {
  const { pathname } = useLocation();
  const [f, setF] = useState({ name: "", website: "", challenge: "", email: "", consent: false, hp_field: "" });
  const [services, setServices] = useState(defaultServices);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle");
  const started = useRef(Date.now());
  const inputRef = useRef(null);
  const first = useRef(true);
  const options = [...defaultServices.filter((s) => !BASE_OPTIONS.includes(s)), ...BASE_OPTIONS];
  const cur = STEPS[step];
  const last = step === STEPS.length - 1;

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    inputRef.current?.focus({ preventScroll: true });
  }, [step]);

  const set = (k) => (e) => { setError(""); setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value })); };
  const toggle = (s) => { setError(""); setServices((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s])); };

  const submit = async () => {
    setStatus("loading");
    try {
      await http.post("/enquiries", {
        name: f.name.trim(), website: f.website.trim(), email: f.email.trim(), challenge: f.challenge.trim(), consent: f.consent,
        services, form_type: formType, source_page: pathname, referrer: document.referrer || "", utm: getAttribution(),
        hp_field: f.hp_field, elapsed_ms: Date.now() - started.current,
      });
      trackLead(formType);
      setStatus("done");
    } catch (err) {
      setError(formatError(err));
      setStatus("idle");
    }
  };

  const next = (e) => {
    e?.preventDefault();
    const err = stepError(cur.key, f, services);
    if (err) { setError(err); return; }
    if (last) submit(); else setStep((s) => s + 1);
  };

  if (status === "done") return <Success name={f.name} />;

  return (
    <form onSubmit={next} noValidate data-testid={`enquiry-form-${formType}`} className="relative rounded-[28px] bg-white p-6 shadow-[0_40px_100px_-40px_rgba(19,5,42,.5)] sm:p-9">
      <Progress step={step} />
      <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }} className="mt-8 min-h-[200px]">
        <label htmlFor={`step-${cur.key}`} className="block text-2xl font-extrabold leading-tight text-[#13052A] sm:text-[28px]" data-testid="form-question">
          {cur.q} {cur.optional && <span className="text-sm font-semibold text-slate-500">(optional)</span>}
        </label>
        {cur.hint && <p className="mt-2 text-sm text-slate-600">{cur.hint}</p>}
        {cur.key === "services" && <Chips options={options} services={services} toggle={toggle} />}
        {cur.key === "challenge" && (
          <textarea id="step-challenge" ref={inputRef} rows={3} className={`${inputCls} mt-4 resize-none !text-lg`} placeholder={cur.placeholder} value={f.challenge} onChange={set("challenge")} data-testid="form-challenge" />
        )}
        {["name", "website", "email"].includes(cur.key) && (
          <input id={`step-${cur.key}`} ref={inputRef} type={cur.key === "email" ? "email" : "text"} inputMode={cur.key === "email" ? "email" : cur.key === "website" ? "url" : "text"}
            autoComplete={cur.auto} className={`${inputCls} mt-4`} placeholder={cur.placeholder} value={f[cur.key]} onChange={set(cur.key)} data-testid={`form-${cur.key}`} />
        )}
        {cur.key === "email" && (
          <label className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-slate-600">
            <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-1 h-5 w-5 shrink-0 accent-[#7C3AED]" data-testid="form-consent" />
            <span>I'm happy for Digital AdDoctor to use these details to reply to my request, as described in the <Link to="/privacy-policy" className="font-semibold text-[#581C87] underline">Privacy Policy</Link>.</span>
          </label>
        )}
      </motion.div>
      <input type="text" tabIndex={-1} autoComplete="off" value={f.hp_field} onChange={set("hp_field")} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden name="company_url" />
      {error && <p role="alert" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="form-error">{error}</p>}
      <div className="mt-8 flex items-center gap-3">
        {step > 0 && (
          <button type="button" onClick={() => { setError(""); setStep((s) => s - 1); }} className="btn-ghost !px-5" data-testid="form-back"><ArrowLeft className="h-4 w-4" /> Back</button>
        )}
        <button type="submit" disabled={status === "loading"} className={`${last ? "btn-accent" : "btn-primary"} flex-1 !py-4 text-base disabled:opacity-70`} data-testid={last ? "form-submit" : "form-next"}>
          {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : last ? <>{submitLabel} <ArrowRight className="h-4 w-4" /></> : <>{cur.optional && !f.challenge ? "Skip" : "Next"} <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
      <p className="mt-4 hidden text-center text-xs text-slate-500 sm:block">Press Enter ↵ to continue</p>
    </form>
  );
};
